/**
 * Utility functions for dynamic box styling based on completion status
 */

export interface BoxCompletionStatus {
  isComplete: boolean;
  isActive: boolean;
  isNext: boolean;
}

/**
 * Get dynamic gradient classes based on box completion status
 */
export function getBoxGradientClasses(status: BoxCompletionStatus): string {
  if (status.isComplete) {
    // Completed boxes get vibrant gaming green with neon glow
    return 'bg-gradient-to-br from-green-600/40 via-emerald-500/30 to-teal-600/20 border-2 border-green-400/60 shadow-lg shadow-green-500/20';
  } else if (status.isActive) {
    // Currently active box gets cyberpunk purple/pink gradient with a very subtle pulse
    return 'bg-gradient-to-br from-purple-600/50 via-fuchsia-500/40 to-pink-600/30 border-2 border-purple-400/70 shadow-lg shadow-purple-500/30 animate-pulse-subtle';
  } else if (status.isNext) {
    // Next box gets fiery orange/red gaming gradient to draw attention
    return 'bg-gradient-to-br from-orange-600/40 via-red-500/30 to-rose-600/20 border-2 border-orange-400/60 shadow-lg shadow-orange-500/20 animate-pulse-subtle';
  } else {
    // Default state with subtle gaming blue tint
    return 'bg-gradient-to-br from-slate-800/40 via-blue-900/20 to-slate-900/30 border border-blue-800/30';
  }
}

/**
 * Get title styling based on box status
 */
export function getBoxTitleClasses(status: BoxCompletionStatus): string {
  if (status.isComplete) {
    return 'text-green-400 font-bold drop-shadow-[0_0_10px_rgba(74,222,128,0.5)]';
  } else if (status.isActive) {
    return 'text-fuchsia-400 font-bold drop-shadow-[0_0_15px_rgba(217,70,239,0.6)]';
  } else if (status.isNext) {
    return 'text-orange-400 font-bold drop-shadow-[0_0_10px_rgba(251,146,60,0.5)]';
  } else {
    return 'text-slate-300';
  }
}

/**
 * Count words in a text string
 */
export function countWords(text: string): number {
  const trimmed = text.trim();
  if (trimmed.length === 0) return 0;
  return trimmed.split(/\s+/).filter((word) => word.length > 0).length;
}

/**
 * Check if a text field meets minimum requirements
 */
export function isFieldComplete(value: string, minLength: number = 10): boolean {
  return value.trim().length >= minLength;
}

/**
 * Check if a text field meets minimum word count
 */
export function isFieldCompleteByWords(value: string, minWords: number = 10): boolean {
  return countWords(value) >= minWords;
}

/**
 * Check if all fields in a group are complete
 */
export function areFieldsComplete(fields: { value: string; minLength?: number }[]): boolean {
  return fields.every((field) => isFieldComplete(field.value, field.minLength));
}
